import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import "react-datepicker/dist/react-datepicker.css";
import baseUrl from "../../../helpers/baseUrl";

const MembershipCard = () => {
  document.title = "Estarch | Membership Cards";

  // States for managing modal and form data
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false); // State for the View Modal
  const [membershipCards, setMembershipCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tier, setTier] = useState("");              // Selected tier
  const [discountPercentage, setDiscountPercentage] = useState("");  // Discount Percentage
  const [selectedCard, setSelectedCard] = useState(null);  // Selected card for editing
  const [viewCard, setViewCard] = useState(null); // Selected card for viewing member info

  // Handle modal toggle
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  // Handle view modal toggle
  const toggleViewModal = () => {
    setIsViewModalOpen(!isViewModalOpen);
  };

  useEffect(() => {
    const fetchMembershipCards = async () => {
      try {
        const response = await fetch(`${baseUrl}/api/memberships`);

        if (!response.ok) {
          throw new Error("Failed to fetch membership cards");
        }

        const data = await response.json();
        setMembershipCards(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMembershipCards();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prepare the data to be sent to the API
    const data = {
      tier,
      discountPercentage,
    };

    try {
      // Send a POST request to the API endpoint
      const response = await fetch(`${baseUrl}/api/memberships/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Make sure to send the data as JSON
        },
        body: JSON.stringify(data), // Convert the data object to a JSON string
      });

      const result = await response.json(); // Parse the response JSON

      // Check if the API response is successful
      if (response.ok) {
        console.log("Card created successfully:", result);
        setTier("");
        setDiscountPercentage("");
        // Close the modal
        toggleModal();
      } else {
        console.error("Error creating card:", result.message);
        // Optionally handle errors (e.g., show an error message)
      }
    } catch (error) {
      console.error("Error:", error);
      // Optionally handle network errors
    }
  };

  const handleEdit = (card) => {
    setSelectedCard(card);
    setTier(card.tier);
    setDiscountPercentage(card.discountPercentage);
    toggleModal();
  };

  const handleView = (card) => {
    setViewCard(card); // Set the card data to be displayed in the view modal
    toggleViewModal(); // Open the view modal
  };

  const handleToggleActive = async (cardId, currentStatus) => {
    if(!currentStatus) return;
    const confirmAction = window.confirm(
      `Are you sure you want to ${currentStatus ? "deactivate" : "activate"} this membership card?`
    );
  
    if (!confirmAction) return; // If the user cancels, do nothing
  
    try {
      // Prepare data to update the card's status
      const data = {
        isActive: !currentStatus, // Toggle status
      };
  
      const response = await fetch(`${baseUrl}/api/memberships/${cardId}/toggle-active`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
  
      const result = await response.json();
  
      if (response.ok) {
        setMembershipCards((prevCards) =>
          prevCards.map((card) =>
            card._id === cardId ? { ...card, isActive: result.isActive, issuedTo: result.isActive ? card.issuedTo : "" } : card
          )
        );
      } else {
        console.error("Failed to update status:", result.message);
        alert(result.message || "Failed to update card status.");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("An error occurred while updating the card status.");
    }
  };
  
  

  return (
    <div className="page-content bg-white">
      <div className="container mx-auto p-6">
        <Breadcrumbs title="Estarch" breadcrumbItem="Membership Cards" />

        <div className="shadow-lg p-6 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <p className="text-xl font-bold">Membership Cards</p>
            <button
              onClick={toggleModal}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              + Add Membership Card
            </button>
          </div>

          {/* Add Membership Card Modal */}
          {isModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                <h2 className="text-2xl font-semibold mb-4">Add Membership Card</h2>
                <form onSubmit={handleSubmit}>
                  {/* Tier Selection */}
                  <div className="mb-4">
                    <label htmlFor="tier" className="block text-sm font-medium text-gray-700">
                      Select Tier
                    </label>
                    <select
                      id="tier"
                      value={tier}
                      onChange={(e) => setTier(e.target.value)}
                      className="w-full mt-2 p-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select Tier</option>
                      <option value="Silver">Silver</option>
                      <option value="Gold">Gold</option>
                      <option value="Platinum">Platinum</option>
                    </select>
                  </div>

                  {/* Discount Percentage */}
                  <div className="mb-4">
                    <label
                      htmlFor="discountPercentage"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Discount Percentage
                    </label>
                    <input
                      type="number"
                      id="discountPercentage"
                      value={discountPercentage}
                      onChange={(e) => setDiscountPercentage(e.target.value)}
                      className="w-full mt-2 p-2 border border-gray-300 rounded-md"
                      placeholder="Enter discount percentage"
                      required
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end mt-4">
                    <button
                      type="submit"
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      Save Card
                    </button>
                    <button
                      type="button"
                      onClick={toggleModal}
                      className="ml-2 bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* View Member Info Modal */}
          {isViewModalOpen && viewCard && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
              <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                <h2 className="text-2xl font-semibold mb-4">Member Info</h2>
                <div>
                  <p><strong>Card Number:</strong> {viewCard?.cardNumber}</p>
                  <p><strong>Tier:</strong> {viewCard?.tier}</p>
                  <p><strong>Discount Percentage:</strong> {viewCard?.discountPercentage}%</p>
                  <p><strong>Issued To:</strong> {viewCard?.issuedTo?.name}</p>
                  <p><strong>Phone:</strong> {viewCard?.issuedTo?.phone}</p>
                  <p><strong>Address:</strong> {viewCard?.issuedTo?.address}</p>
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    type="button"
                    onClick={toggleViewModal}
                    className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table for Membership Cards */}
          <div className="overflow-x-auto mt-6">
            {loading ? (
              <p>Loading...</p>
            ) : error ? (
              <p className="text-red-500">{error}</p>
            ) : (
              <table className="min-w-full table-auto">
                <thead>
                  <tr>
                    <th className="border px-4 py-2">Card Number</th>
                    <th className="border px-4 py-2">Tier</th>
                    <th className="border px-4 py-2">Discount</th>
                    <th className="border px-4 py-2">Active</th>
                    <th className="border px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {membershipCards.map((card) => (
                    <tr key={card._id}>
                      <td className="border px-4 py-2">{card.cardNumber}</td>
                      <td className="border px-4 py-2">{card.tier}</td>
                      <td className="border px-4 py-2">{card.discountPercentage}%</td>
                      <td className="border px-4 py-2">
                        <button
                          onClick={() => handleToggleActive(card._id, card.isActive)}
                          className={`px-4 py-2 rounded ${card.isActive ? 'bg-green-500' : 'bg-red-500'} text-white`}
                        >
                          {card.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="border px-4 py-2">
                        <button
                          onClick={() => handleEdit(card)}
                          className="bg-blue-600 text-white px-4 py-2 rounded"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleView(card)} // View Button
                          className="bg-green-600 text-white px-4 py-2 rounded ml-2"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipCard;
